import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import MaterialLinearProgress from '@mui/material/LinearProgress';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  FDBodyText,
  FDBox, FDIcon, FDStack, FDTooltip,
} from '@facet-wealth/design';
import { getBorderRadius } from 'Components/utils';
import { getThresholdColorMap } from 'Consts/thresholds';
import { dataAttrs } from 'Helpers/Testing';

const ICON_SIZE = 16;
export const PROGRESS_BAR_SIZE = 10;
const ICON_PADDING = 7;

const useStyles = buildUseStyles('ProgressWithIcon', (theme) => ({
  root: {
    width: ({ width }) => width,
    position: 'relative',
  },
  circle: {
    ...getBorderRadius(theme.radius.full, { all: true }),
    padding: ICON_PADDING,
    position: 'absolute',
    top: (PROGRESS_BAR_SIZE - ICON_SIZE) / 2 - ICON_PADDING,
    display: 'flex',
    // Handle FontAwesome icons
    '& > div': {
      display: 'inline-flex',
      '& > .FD-MuiSvgIcon-root': {
        color: `${theme.palette.common.white} !important`,
      },
    },
    // Handle MUI icons
    '& > .FD-MuiSvgIcon-root': {
      color: `${theme.palette.common.white} !important`,
    },
  },
  ...(Object.fromEntries(Object.entries(getThresholdColorMap(theme)).map(([colorName, color]) => ([
    `circle${colorName}`,
    { backgroundColor: color },
  ])))),
}));

export const ProgressWithIcon = ({
  totalValue, progressValue, icon, color, width, tooltipTitle, tooltipBody = undefined, leftAlignedDescription, rightAlignedDescription,
}) => {
  const classes = useStyles({ width });

  const getLeftIconStyleValue = () => {
    const valueAndWidth = totalValue / 100 * width;
    const iconSizeAndPadding = ICON_SIZE + (ICON_PADDING * 2);
    if (valueAndWidth < iconSizeAndPadding) {
      return valueAndWidth;
    }
    return valueAndWidth - iconSizeAndPadding;
  };

  let IconComponent = (
    <div
      className={classNames(classes.circle, classes[`circle${color}`])}
      style={{ left: getLeftIconStyleValue() }}
    >
      <FDIcon icon={icon} size="sm" />
    </div>);
  if (tooltipTitle) {
    IconComponent = (
      <FDTooltip placement={'top'} title={tooltipTitle} body={tooltipBody}>
        {IconComponent}
      </FDTooltip>
    );
  }

  const hasDescriptions = leftAlignedDescription || rightAlignedDescription;

  return (
    <div className={classes.root} {...dataAttrs('progress-bar')}>
      <FDStack direction={'column'} spacing={1.5}>
        <FDBox>
          <MaterialLinearProgress
            sx={{
              height: `${PROGRESS_BAR_SIZE}px`,
            }}
            value={progressValue}
            variant="determinate"
          />
          {IconComponent}
        </FDBox>
        {hasDescriptions
          ? (
            <FDStack direction={'row'} justifyContent={'space-between'}>
              <FDBodyText size={'sm'} value={leftAlignedDescription} />
              <FDBodyText size={'sm'} value={rightAlignedDescription} />
            </FDStack>)
          : null
        }
      </FDStack>
    </div>
  );
};

ProgressWithIcon.defaultProps = {
  width: 200,
  progressValue: 0,
  tooltipTitle: undefined,
  leftAlignedDescription: undefined,
  rightAlignedDescription: undefined,
};

ProgressWithIcon.propTypes = {
  progressValue: PropTypes.number,
  totalValue: PropTypes.number.isRequired,
  icon: PropTypes.string,
  color: PropTypes.string,
  width: PropTypes.number,
  tooltipTitle: PropTypes.string,
  leftAlignedDescription: PropTypes.string,
  rightAlignedDescription: PropTypes.string,
};
